/**
 * NOTE:
 * - 운영 환경에서는 API proxy가 `x-missionfund-org` 헤더를 서버로 전달할 때 주입합니다.
 * - 프론트에서는 보안상 해당 헤더를 직접 보내지 않습니다.
 *
 * Local quick test:
 * - env 없이 바로 테스트하려면 아래 DEFAULT를 서버 proxy 주소로 두면 됩니다.
 * - 필요하면 `/Prod` 유무만 서버 proxy 설정에 맞게 조정하세요.
 */
import { getAccessToken, setAccessToken } from './tokenStorage';

const DEFAULT_API_BASE = "https://sapi.missionfund.org";
const AUTH_API_BASE = (process.env.REACT_APP_API_BASE || DEFAULT_API_BASE).replace(/\/$/, "");

export class AuthApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type HttpMethod = "POST";

const defaultHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

export async function authRequest<TResponse>(
  path: string,
  options: { method?: HttpMethod; body?: unknown; withAccessToken?: boolean } = {}
): Promise<TResponse> {
  const { method = "POST", body, withAccessToken = true } = options;
  const accessToken = getAccessToken();
  const headers: Record<string, string> = {
    ...defaultHeaders,
  };
  if (withAccessToken && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${AUTH_API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  const responseBody = await response.json().catch(() => undefined);

  if (!response.ok) {
    const body = responseBody as { message?: string; result_message?: string | unknown };
    const resultMsg = body?.result_message;
    const message =
      body?.message ??
      (typeof resultMsg === 'string' ? resultMsg : undefined) ??
      `Auth API error ${response.status} on ${path}`;
    throw new AuthApiError(message, response.status, responseBody);
  }

  return responseBody as TResponse;
}

type RefreshResponse = {
  result_Code?: number;
  token?: {
    accessToken?: string;
  };
};

export async function refreshAccessToken(): Promise<string> {
  const res = await authRequest<RefreshResponse>('/auth/refresh');
  const nextToken = res?.token?.accessToken ?? '';
  if (!nextToken) {
    throw new AuthApiError('Refresh token required', 401, res);
  }
  setAccessToken(nextToken);
  return nextToken;
}

export async function logoutAuth(): Promise<void> {
  await authRequest('/auth/logout');
}
